/**
 * Filter bar + view toggle + saved views + sort menu + reset.
 *
 * Pure UI — drives the radar state hook via the props from the page.
 *
 * Filter option lists (dept / BU / status) are derived per-tenant by the
 * hook and passed in as props. We keep a tiny safety fallback for the
 * status list so the toolbar renders even on a brand-new tenant with no
 * opportunities yet.
 */
import React from "react";
import {
  LayoutGrid,
  Rows3,
  ScatterChart,
  Search,
  ChevronDown,
  Bookmark,
  ArrowDownAZ,
  X,
} from "lucide-react";
import { OPPORTUNITY_STATUSES } from "@/lib/demoData";
import { cn } from "@/lib/utils";

const VIEWS = [
  { id: "matrix", label: "Matrix", icon: ScatterChart },
  { id: "table", label: "Table", icon: Rows3 },
  { id: "card", label: "Card", icon: LayoutGrid },
];

const STATUS_OPTIONS = [
  { value: "all", label: "All statuses" },
  ...OPPORTUNITY_STATUSES.map((s) => ({ value: s, label: s })),
];
const RISK_OPTIONS = [
  { value: "all", label: "All risk" },
  { value: "low", label: "Low" },
  { value: "med", label: "Medium" },
  { value: "high", label: "High" },
];
const EFFORT_OPTIONS = [
  { value: "all", label: "All effort" },
  { value: "S", label: "Small" },
  { value: "M", label: "Medium" },
  { value: "L", label: "Large" },
];
const COMPLIANCE_OPTIONS = [
  { value: "all", label: "All impact" },
  { value: "low", label: "Low" },
  { value: "med", label: "Medium" },
  { value: "high", label: "High" },
];

export function RadarToolbar({
  filters,
  updateFilter,
  resetFilters,
  view,
  setView,
  sort,
  setSort,
  sortOptions,
  savedViews,
  activeSavedView,
  applySavedView,
  matchedCount,
  totalCount,
  deptOptions,
  buOptions,
}) {
  const hasActiveFilters =
    filters.q !== "" ||
    filters.department !== "all" ||
    filters.businessUnit !== "all" ||
    filters.status !== "all" ||
    filters.risk !== "all" ||
    filters.effort !== "all" ||
    filters.complianceImpact !== "all";

  return (
    <div className="surface-card shadow-e1 p-3" data-testid="radar-toolbar">
      {/* Row 1 — search · saved views · sort · view toggle */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search
            size={14}
            className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-secondary"
          />
          <input
            type="search"
            value={filters.q}
            onChange={(e) => updateFilter("q", e.target.value)}
            placeholder="Search opportunities…"
            className="w-full h-9 pl-8 pr-3 bg-inset border border-transparent rounded-md text-sm placeholder:text-ink-disabled focus:bg-raised focus:border-tralenex-300 focus:ring-2 focus:ring-tralenex-200 outline-none"
            data-testid="radar-search-input"
          />
        </div>

        <SavedViewsMenu
          savedViews={savedViews}
          activeSavedView={activeSavedView}
          applySavedView={applySavedView}
        />

        <SortMenu sort={sort} setSort={setSort} options={sortOptions} />

        <div className="ml-auto inline-flex items-center rounded-md border border-border-subtle bg-raised p-0.5">
          {VIEWS.map((v) => (
            <button
              key={v.id}
              type="button"
              onClick={() => setView(v.id)}
              className={cn(
                "inline-flex items-center gap-1.5 px-2.5 h-7 text-meta font-semibold rounded transition-colors",
                view === v.id
                  ? "bg-tralenex-600 text-white"
                  : "text-ink-secondary hover:text-ink-primary",
              )}
              data-testid={`radar-view-${v.id}`}
              aria-pressed={view === v.id}
            >
              <v.icon size={12} /> {v.label}
            </button>
          ))}
        </div>
      </div>

      {/* Row 2 — segmented filter chips */}
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <FilterSelect
          testId="radar-filter-department"
          label="Department"
          value={filters.department}
          onChange={(v) => updateFilter("department", v)}
          options={deptOptions}
        />
        <FilterSelect
          testId="radar-filter-businessUnit"
          label="Business Unit"
          value={filters.businessUnit}
          onChange={(v) => updateFilter("businessUnit", v)}
          options={buOptions}
        />
        <FilterSelect
          testId="radar-filter-status"
          label="Status"
          value={filters.status}
          onChange={(v) => updateFilter("status", v)}
          options={STATUS_OPTIONS}
        />
        <FilterSelect
          testId="radar-filter-risk"
          label="Risk"
          value={filters.risk}
          onChange={(v) => updateFilter("risk", v)}
          options={RISK_OPTIONS}
        />
        <FilterSelect
          testId="radar-filter-effort"
          label="Effort"
          value={filters.effort}
          onChange={(v) => updateFilter("effort", v)}
          options={EFFORT_OPTIONS}
        />
        <FilterSelect
          testId="radar-filter-complianceImpact"
          label="Compliance"
          value={filters.complianceImpact}
          onChange={(v) => updateFilter("complianceImpact", v)}
          options={COMPLIANCE_OPTIONS}
        />

        <div className="ml-auto flex items-center gap-2">
          <span className="text-meta text-ink-secondary tabular-nums" data-testid="radar-match-count">
            {matchedCount} of {totalCount}
          </span>
          {hasActiveFilters && (
            <button
              type="button"
              onClick={resetFilters}
              className="inline-flex items-center gap-1 px-2 h-7 rounded-md text-meta font-medium text-ink-secondary hover:text-ink-primary hover:bg-inset transition-colors"
              data-testid="radar-reset-filters"
            >
              <X size={11} /> Reset filters
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function FilterSelect({ testId, label, value, onChange, options }) {
  return (
    <label className="inline-flex items-center gap-1.5 text-meta">
      <span className="text-ink-secondary">{label}</span>
      <div className="relative">
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="h-7 pl-2 pr-6 rounded-md border border-border-subtle bg-raised text-sm text-ink-primary appearance-none focus:border-tralenex-300 focus:ring-2 focus:ring-tralenex-200 outline-none"
          data-testid={testId}
        >
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <ChevronDown
          size={11}
          className="absolute right-1.5 top-1/2 -translate-y-1/2 text-ink-secondary pointer-events-none"
        />
      </div>
    </label>
  );
}

function SortMenu({ sort, setSort, options }) {
  return (
    <div className="relative">
      <label className="inline-flex items-center gap-1.5 text-meta">
        <ArrowDownAZ size={13} className="text-ink-secondary" />
        <select
          value={sort.field}
          onChange={(e) =>
            setSort(options.find((o) => o.field === e.target.value) || options[0])
          }
          className="h-9 pl-2 pr-7 rounded-md border border-border-subtle bg-raised text-sm text-ink-primary appearance-none focus:border-tralenex-300 focus:ring-2 focus:ring-tralenex-200 outline-none"
          data-testid="radar-sort-select"
        >
          {options.map((o) => (
            <option key={o.field} value={o.field}>
              {`Sort · ${o.label}`}
            </option>
          ))}
        </select>
        <ChevronDown
          size={12}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-ink-secondary pointer-events-none"
        />
      </label>
    </div>
  );
}

function SavedViewsMenu({ savedViews, activeSavedView, applySavedView }) {
  return (
    <div className="relative">
      <label className="inline-flex items-center gap-1.5 text-meta">
        <Bookmark size={13} className="text-gold-600" />
        <select
          value={activeSavedView || ""}
          onChange={(e) => e.target.value && applySavedView(e.target.value)}
          className="h-9 pl-2 pr-7 rounded-md border border-border-subtle bg-raised text-sm text-ink-primary appearance-none focus:border-tralenex-300 focus:ring-2 focus:ring-tralenex-200 outline-none"
          data-testid="radar-saved-views-select"
        >
          <option value="">Saved views</option>
          {savedViews.map((sv) => (
            <option key={sv.id} value={sv.id}>
              {sv.label}
            </option>
          ))}
        </select>
        <ChevronDown
          size={12}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-ink-secondary pointer-events-none"
        />
      </label>
    </div>
  );
}
